import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, FileText, MessageSquare, Calendar, Music, Video, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import VinylLoader from "@/components/site/VinylLoader";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const hideTimer = useRef(null);

  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  const reveal = () => {
    setRevealed(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setRevealed(false), 30000);
  };
  const hide = () => {
    setRevealed(false);
    if (hideTimer.current) clearTimeout(hideTimer.current);
  };

  if (!stats) return <div className="flex justify-center py-20"><VinylLoader size={70} label="Loading dashboard" /></div>;

  const cards = [
    { icon: TrendingUp, label: "Revenue", value: `$${stats.revenue_total.toFixed(2)}`, sub: `${stats.payments_count} successful payments`, accent: true, sensitive: true },
    { icon: FileText, label: "Active Contracts", value: stats.contracts_active, sub: `${stats.contracts_completed} completed` },
    { icon: MessageSquare, label: "New Inquiries", value: stats.video_inquiries_new + stats.distribution_inquiries_new, sub: `${stats.video_inquiries_new} video / ${stats.distribution_inquiries_new} distro` },
    { icon: Calendar, label: "Scheduled Posts", value: stats.scheduled_posts, sub: "across IG / FB / Email" },
    { icon: Music, label: "Artists on Roster", value: stats.artists },
    { icon: Video, label: "Videos in Catalog", value: stats.videos },
  ];

  return (
    <div data-testid="admin-overview">
      <div className="label-mono mb-2">/ Overview</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-10">Mission Control</h1>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c, i) => {
          const isBlurred = c.sensitive && !revealed;
          return (
            <motion.div
              key={c.label}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className={`relative p-7 rounded-2xl border bg-[#121215] ${c.accent ? "border-cyan-500/25" : "border-white/5"}`}
              data-testid={`stat-${c.label.toLowerCase().replace(/\s/g,'-')}`}
            >
              <c.icon size={20} strokeWidth={1.5} className={c.accent ? "text-cyan-400" : "text-zinc-400"} />
              <div className="label-mono mt-5 mb-1">{c.label}</div>
              <div
                className={`font-display text-3xl font-bold tracking-tight transition-all duration-300 ${isBlurred ? "blur-lg select-none" : ""}`}
                aria-hidden={isBlurred}
                data-testid={c.sensitive ? "revenue-value" : undefined}
              >
                {c.value}
              </div>
              {c.sub && (
                <div className={`text-zinc-500 text-xs mt-2 font-mono transition ${isBlurred ? "blur-sm" : ""}`}>
                  {c.sub}
                </div>
              )}
              {c.sensitive && (
                <>
                  <AnimatePresence>
                    {isBlurred && (
                      <motion.button
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={reveal}
                        className="absolute inset-0 flex items-center justify-center rounded-2xl group"
                        data-testid="reveal-revenue-btn"
                      >
                        <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-black/70 border border-cyan-400/40 text-cyan-300 text-[11px] font-black tracking-widest group-hover:bg-black/90 group-hover:scale-[1.03] transition">
                          <Eye size={13}/> REVEAL REVENUE
                        </span>
                      </motion.button>
                    )}
                  </AnimatePresence>
                  {revealed && (
                    <button
                      onClick={hide}
                      className="absolute top-4 right-4 p-1.5 rounded-md bg-black/40 border border-white/10 text-zinc-500 hover:text-cyan-300 hover:border-cyan-400/40 transition"
                      title="Hide again"
                      data-testid="hide-revenue-btn"
                    >
                      <EyeOff size={12}/>
                    </button>
                  )}
                </>
              )}
            </motion.div>
          );
        })}
      </div>
      {revealed && (
        <div className="text-[10px] text-zinc-500 font-mono tracking-widest mt-4 text-right">
          / AUTO-HIDES IN 30s
        </div>
      )}
    </div>
  );
}
